import { useState } from 'react';
import { Card } from '../components/Ui';

const models = [
  {
    id: 'da-lite',
    name: 'DuaAxis Lite',
    tagline: 'Residential & Small Commercial',
    badge: 'Entry Level',
    color: 'from-blue-500 to-blue-600',
    specs: {
      powerRange: '2 – 20 kW',
      trackingAxes: 'Dual-Axis (Az + El)',
      trackingAccuracy: '±0.1°',
      windResistance: '120 km/h operational / 200 km/h survival',
      operatingTemp: '-20°C to +70°C',
      controlSystem: 'GPS + Astronomical Algorithm',
      communication: 'Wi-Fi / RS485 Modbus',
      warranty: '10 years mechanical / 5 years electrical',
      weight: '180 kg per tracker unit',
      dimensions: '2.4 m × 1.8 m (single panel)',
      certifications: ['IEC 61215', 'CE', 'ISO 9001'],
      material: 'Hot-dip galvanised steel + anodised Al',
    },
  },
  {
    id: 'da-pro',
    name: 'DuaAxis Pro',
    tagline: 'Commercial & Industrial',
    badge: 'Most Popular',
    color: 'from-primary-500 to-primary-600',
    specs: {
      powerRange: '20 – 500 kW',
      trackingAxes: 'Dual-Axis (Az + El)',
      trackingAccuracy: '±0.05°',
      windResistance: '140 km/h operational / 220 km/h survival',
      operatingTemp: '-30°C to +75°C',
      controlSystem: 'GPS + Weather-Adaptive AI',
      communication: 'LAN / 4G / RS485 Modbus / SCADA',
      warranty: '15 years mechanical / 10 years electrical',
      weight: '420 kg per tracker unit',
      dimensions: '4.8 m × 3.2 m (multi-panel)',
      certifications: ['IEC 61215', 'IEC 61730', 'CE', 'ISO 9001', 'ISO 14001'],
      material: 'Marine-grade anodised aluminium frame',
    },
  },
  {
    id: 'da-max',
    name: 'DuaAxis Max',
    tagline: 'Utility Scale',
    badge: 'High Power',
    color: 'from-purple-500 to-purple-600',
    specs: {
      powerRange: '500 kW – 5+ MW',
      trackingAxes: 'Dual-Axis (Az + El)',
      trackingAccuracy: '±0.02°',
      windResistance: '160 km/h operational / 250 km/h survival',
      operatingTemp: '-40°C to +80°C',
      controlSystem: 'RTK-GPS + AI Weather Prediction + Cloud SCADA',
      communication: 'Fibre / 4G / Satellite / SCADA integration',
      warranty: '25 years mechanical / 15 years electrical',
      weight: '1,200 kg per tracker unit',
      dimensions: '9.6 m × 6.4 m (utility array)',
      certifications: ['IEC 61215', 'IEC 61730', 'CE', 'ISO 9001', 'ISO 14001', 'UL 2703', 'MCS'],
      material: 'High-strength structural steel with corrosion coating',
    },
  },
];

const comparisonRows = [
  { label: 'Power Range', da_lite: '2–20 kW', da_pro: '20–500 kW', da_max: '500 kW–5+ MW' },
  { label: 'Tracking Accuracy', da_lite: '±0.1°', da_pro: '±0.05°', da_max: '±0.02°' },
  { label: 'Wind Resistance (Op)', da_lite: '120 km/h', da_pro: '140 km/h', da_max: '160 km/h' },
  { label: 'AI Weather Adaptation', da_lite: '—', da_pro: '✓', da_max: '✓ Advanced' },
  { label: 'SCADA Integration', da_lite: '—', da_pro: '✓', da_max: '✓ Full' },
  { label: 'Remote Monitoring', da_lite: 'App only', da_pro: 'App + Web', da_max: 'App + Web + API' },
  { label: 'Automatic Dust Clean', da_lite: 'Optional', da_pro: 'Optional', da_max: 'Included' },
  { label: 'Mechanical Warranty', da_lite: '10 years', da_pro: '15 years', da_max: '25 years' },
];

const certifications = [
  { name: 'IEC 61215', body: 'IEC', desc: 'Terrestrial PV module design qualification and type approval', icon: '🏅' },
  { name: 'IEC 61730', body: 'IEC', desc: 'Photovoltaic module safety qualification', icon: '🔒' },
  { name: 'CE Marking', body: 'EU', desc: 'European conformity — safety, health, and environmental protection', icon: '🇪🇺' },
  { name: 'ISO 9001:2015', body: 'ISO', desc: 'Quality management systems certification', icon: '⭐' },
  { name: 'ISO 14001:2015', body: 'ISO', desc: 'Environmental management systems certification', icon: '🌿' },
  { name: 'UL 2703', body: 'UL', desc: 'Mounting systems and grounding for photovoltaic panels', icon: '🔌' },
];

const downloads = [
  { title: 'DuaAxis Lite Datasheet', size: '1.2 MB', type: 'PDF', icon: '📄' },
  { title: 'DuaAxis Pro Datasheet', size: '1.8 MB', type: 'PDF', icon: '📄' },
  { title: 'DuaAxis Max Datasheet', size: '2.4 MB', type: 'PDF', icon: '📄' },
  { title: 'Installation Manual v3.2', size: '8.6 MB', type: 'PDF', icon: '📋' },
  { title: 'Modbus RTU Register Map', size: '340 KB', type: 'PDF', icon: '📊' },
  { title: 'SCADA Integration Guide', size: '2.1 MB', type: 'PDF', icon: '🖥️' },
];

const Technical = () => {
  const [selectedModel, setSelectedModel] = useState('da-pro');
  const activeModel = models.find(m => m.id === selectedModel);

  return (
    <div className="min-h-screen pt-16 bg-gray-50">
      {/* Hero */}
      <div className="bg-gradient-to-r from-primary-600 to-primary-800 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-block bg-white/20 text-white text-sm font-semibold py-2 px-4 rounded-full mb-4">
            Technical Specifications
          </span>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Engineering Documentation</h1>
          <p className="text-xl text-primary-100 max-w-3xl mx-auto">
            Full technical specifications, certifications, and integration guides for DuaAxis solar tracking systems
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">

        {/* Model Selector */}
        <div className="flex flex-wrap gap-4 justify-center mb-12">
          {models.map(m => (
            <button
              key={m.id}
              onClick={() => setSelectedModel(m.id)}
              className={`px-6 py-3 rounded-xl font-semibold transition-all duration-200 border-2 ${
                selectedModel === m.id
                  ? 'border-primary-600 bg-primary-600 text-white shadow-lg'
                  : 'border-gray-200 bg-white text-gray-700 hover:border-primary-300'
              }`}
            >
              {m.name}
              <span className={`ml-2 text-xs px-2 py-0.5 rounded-full ${
                selectedModel === m.id ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-500'
              }`}>
                {m.badge}
              </span>
            </button>
          ))}
        </div>

        {/* Active Model Specs */}
        {activeModel && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-16">
            {/* Model Header */}
            <div className={`bg-gradient-to-br ${activeModel.color} text-white rounded-2xl p-8`}>
              <h2 className="text-3xl font-bold mb-2">{activeModel.name}</h2>
              <p className="text-white/80 mb-6">{activeModel.tagline}</p>
              <div className="text-4xl font-bold mb-1">{activeModel.specs.powerRange}</div>
              <p className="text-sm text-white/70">Capacity Range</p>
              <div className="mt-8 space-y-3">
                {activeModel.specs.certifications.map(cert => (
                  <span key={cert} className="inline-block bg-white/20 text-white text-xs font-semibold px-3 py-1 rounded-full mr-2">
                    {cert}
                  </span>
                ))}
              </div>
            </div>

            {/* Specs Table */}
            <div className="lg:col-span-2 bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
              <div className="bg-gray-50 px-6 py-4 border-b border-gray-100">
                <h3 className="font-semibold text-gray-900">Technical Specifications</h3>
              </div>
              <div className="divide-y divide-gray-50">
                {Object.entries(activeModel.specs)
                  .filter(([key]) => key !== 'certifications')
                  .map(([key, value]) => {
                    const labels = {
                      powerRange: 'Power Range',
                      trackingAxes: 'Tracking Axes',
                      trackingAccuracy: 'Tracking Accuracy',
                      windResistance: 'Wind Resistance',
                      operatingTemp: 'Operating Temperature',
                      controlSystem: 'Control System',
                      communication: 'Communication',
                      warranty: 'Warranty',
                      weight: 'Weight',
                      dimensions: 'Dimensions',
                      material: 'Frame Material',
                    };
                    return (
                      <div key={key} className="flex px-6 py-3 hover:bg-gray-50 transition-colors">
                        <span className="w-48 text-sm text-gray-500 flex-shrink-0">{labels[key] || key}</span>
                        <span className="text-sm font-medium text-gray-900">{value}</span>
                      </div>
                    );
                  })}
              </div>
            </div>
          </div>
        )}

        {/* Comparison Table */}
        <div className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 text-center mb-8">Model Comparison</h2>
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="text-left px-6 py-4 text-gray-500 font-semibold w-48">Specification</th>
                  {models.map(m => (
                    <th key={m.id} className="px-6 py-4 text-center">
                      <span className={`inline-block bg-gradient-to-r ${m.color} text-white text-sm font-semibold px-3 py-1 rounded-full`}>
                        {m.name}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {comparisonRows.map((row, i) => (
                  <tr key={row.label} className={i % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                    <td className="px-6 py-3 font-medium text-gray-700">{row.label}</td>
                    <td className="px-6 py-3 text-center text-gray-600">{row.da_lite}</td>
                    <td className="px-6 py-3 text-center text-primary-600 font-medium">{row.da_pro}</td>
                    <td className="px-6 py-3 text-center text-purple-600 font-medium">{row.da_max}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Certifications */}
        <div className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 text-center mb-8">Certifications & Standards</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {certifications.map(cert => (
              <Card key={cert.name} variant="elevated" padding="medium">
                <div className="flex items-start gap-4">
                  <span className="text-3xl">{cert.icon}</span>
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-bold text-gray-900">{cert.name}</h3>
                      <span className="bg-primary-100 text-primary-700 text-xs px-2 py-0.5 rounded-full">{cert.body}</span>
                    </div>
                    <p className="text-sm text-gray-600">{cert.desc}</p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>

        {/* Downloads */}
        <div className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 text-center mb-8">Technical Downloads</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {downloads.map(doc => (
              <div
                key={doc.title}
                className="bg-white border border-gray-200 rounded-xl p-5 flex items-center gap-4 hover:border-primary-300 hover:shadow-md transition-all duration-200 cursor-pointer group"
              >
                <span className="text-3xl">{doc.icon}</span>
                <div className="flex-1">
                  <p className="font-medium text-gray-900 group-hover:text-primary-600 transition-colors">{doc.title}</p>
                  <p className="text-xs text-gray-400">{doc.type} · {doc.size}</p>
                </div>
                <svg className="w-5 h-5 text-gray-400 group-hover:text-primary-500 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
              </div>
            ))}
          </div>
          <p className="text-center text-sm text-gray-400 mt-4">
            Contact <a href="mailto:info@duaaxis.com" className="text-primary-600 hover:underline">info@duaaxis.com</a> for additional documentation or custom engineering requests.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Technical;
